/**
 * BrightPath - BudgetForm Component
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DollarSign, AlertTriangle } from 'lucide-react';
import type { Budget, BudgetCreate, BudgetUpdate, ExpenseCategory } from '../types';
import { EXPENSE_CATEGORIES, getCurrentBudgetPeriod, formatBudgetPeriod } from '../types';

const budgetSchema = z.object({
  category: z.enum([
    'medical', 'therapy', 'equipment', 'medication', 'transportation',
    'home_modification', 'legal', 'insurance', 'other',
  ]),
  monthlyBudget: z.number().min(1, 'Budget must be greater than 0'),
  year: z.number().min(2020).max(2100),
  month: z.number().min(1).max(12),
  alertThreshold: z.number().min(0).max(100).default(80),
});

type BudgetFormData = z.infer<typeof budgetSchema>;

interface BudgetFormProps {
  budget?: Budget;
  existingCategories?: ExpenseCategory[];
  onSubmit: (data: BudgetCreate | BudgetUpdate) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function BudgetForm({
  budget,
  existingCategories = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
}: BudgetFormProps) {
  const { year: currentYear, month: currentMonth } = getCurrentBudgetPeriod();

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<BudgetFormData>({
    resolver: zodResolver(budgetSchema),
    defaultValues: {
      category: budget?.category || 'medical',
      monthlyBudget: budget?.monthlyBudget || 500,
      year: budget?.year || currentYear,
      month: budget?.month || currentMonth,
      alertThreshold: budget?.alertThreshold || 80,
    },
  });

  const watchYear = watch('year');
  const watchMonth = watch('month');
  const watchCategory = watch('category');

  const availableCategories = budget
    ? Object.entries(EXPENSE_CATEGORIES)
    : Object.entries(EXPENSE_CATEGORIES).filter(
        ([key]) => !existingCategories.includes(key as ExpenseCategory)
      );

  const onFormSubmit = async (data: BudgetFormData) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">Category *</label>
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              disabled={!!budget}
              className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors disabled:opacity-50"
            >
              {availableCategories.map(([value, config]) => (
                <option key={value} value={value}>
                  {config.label}
                </option>
              ))}
            </select>
          )}
        />
        {errors.category && (
          <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
        )}
      </div>

      {/* Period */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Year *</label>
          <Controller
            name="year"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                onChange={(e) => field.onChange(parseInt(e.target.value))}
                disabled={!!budget}
                className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors disabled:opacity-50"
              >
                {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-navy mb-2">Month *</label>
          <Controller
            name="month"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                onChange={(e) => field.onChange(parseInt(e.target.value))}
                disabled={!!budget}
                className="w-full px-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors disabled:opacity-50"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <option key={month} value={month}>
                    {new Date(2000, month - 1).toLocaleDateString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
      </div>

      {/* Monthly Budget */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">Monthly Budget *</label>
        <div className="relative">
          <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
          <Controller
            name="monthlyBudget"
            control={control}
            render={({ field }) => (
              <input
                type="number"
                min="1"
                step="0.01"
                {...field}
                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                className="w-full pl-12 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
              />
            )}
          />
        </div>
        {errors.monthlyBudget && (
          <p className="text-red-500 text-sm mt-1">{errors.monthlyBudget.message}</p>
        )}
      </div>

      {/* Alert Threshold */}
      <div>
        <label className="block text-sm font-medium text-navy mb-2">
          Alert Threshold (%)
        </label>
        <div className="relative">
          <AlertTriangle className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
          <Controller
            name="alertThreshold"
            control={control}
            render={({ field }) => (
              <input
                type="number"
                min="0"
                max="100"
                {...field}
                onChange={(e) => field.onChange(parseInt(e.target.value) || 80)}
                className="w-full pl-12 pr-4 py-3 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
              />
            )}
          />
        </div>
        <p className="text-sm text-warm-gray mt-1">
          You'll be alerted when spending reaches this percentage of the budget
        </p>
      </div>

      {/* Preview */}
      <div className="p-4 bg-cream rounded-lg">
        <p className="text-sm text-warm-gray mb-1">Budget Preview</p>
        <p className="text-lg font-semibold text-navy">
          {EXPENSE_CATEGORIES[watchCategory as ExpenseCategory]?.label} -{' '}
          {formatBudgetPeriod(watchYear, watchMonth)}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 text-navy hover:bg-cream-dark rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : budget ? 'Update Budget' : 'Create Budget'}
        </button>
      </div>
    </form>
  );
}

export default BudgetForm;
